// Full-page loads that must not end a recording, and must not surprise anyone.
//
// The LiveScribe WebSocket and the microphone belong to this page, so any
// reload or redirect ends a recording mid-visit (2026-09-30: a provider
// switched the working location in the header to read the other location's
// chat, the page reloaded, and the visit recording stopped). Owner rule: a
// recording is never ended by the app, and the app never reloads by surprise.
// Every full-page load the app starts itself goes through here:
//
//   - nothing in progress: it happens at once, exactly as before;
//   - otherwise it is queued. "In progress" (busy) is a visit recording, its
//     note still processing after Stop, or a local recording some screen holds
//     (Edit with Voice, a chat voice message: holdNavigation()). A load is also
//     queued while the open chart shows a note made during this chart session
//     (a reload would pull the finished note away).
//
// A queued load never runs just because the recording ended (that moment is
// arbitrary: the provider may be typing or mid-form). It runs only:
//   - when the provider clicks the notice's button (Refresh / Sign in now),
//     offered once nothing is busy; or
//   - at the next natural boundary, if nothing is busy, the chart is closed
//     and the provider is not mid-task (a text field focused, a dialog open):
//     the chart closing, a page change, or the tab becoming visible again.
// Never on focusout/blur: that runs between the mousedown and the click on a
// button such as chat Send, and the click would be lost.
//
// RecordingContext reports recording / processing / chart-open through
// setRecordingState(). At most one load is queued: a redirect (to sign in)
// replaces a queued reload, since it loads a fresh page as well.
//
// Pure module (no React): unit tested under node,
//   node --no-warnings src/utils/deferredNavigation.test.mjs

let rec = { recording: false, processing: false, chartOpen: false, chartNote: false };
// Local recordings in progress outside RecordingContext (holdNavigation()).
let localHolds = 0;
// { kind: "reload", reason, subject, verb }
// | { kind: "redirect", href, replace, reason }
let pending = null;
const listeners = new Set();

// The browser's own navigation calls, replaceable in tests.
export const browser = {
  reload: () => window.location.reload(),
  replace: (href) => window.location.replace(href),
  assign: (href) => {
    window.location.href = href;
  },
};

// Listeners hear about every change of the queue or of what it waits for.
const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn(pending);
    } catch (_) {
      /* a listener's error must not block navigation */
    }
  });
};

// RecordingContext also mirrors the recording to window.__bkRecordingActive;
// either one counts.
export const isRecordingActive = () =>
  rec.recording ||
  (typeof window !== "undefined" && window.__bkRecordingActive === true);

// What a queued load is waiting for: "recording" (a visit recording),
// "voice" (a local recording: voice edit, chat voice message), "note" (the
// stopped recording's note is still processing), or "" (nothing).
export function busyReason() {
  if (isRecordingActive()) return "recording";
  if (localHolds > 0) return "voice";
  if (rec.processing) return "note";
  return "";
}

export const isNavigationBusy = () => busyReason() !== "";

export const hasLocalRecording = () => localHolds > 0;

export const getNavigationState = () => ({ ...rec, localHolds });

const mustDefer = () => isNavigationBusy() || (rec.chartOpen && rec.chartNote);

const go = (href, replace) => {
  if (replace) browser.replace(href);
  else browser.assign(href);
};

let visibilityArmed = false;
const onVisibilityChange = () => {
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    navigationBoundary("visible");
  }
};
const setPending = (p) => {
  pending = p;
  if (typeof document !== "undefined") {
    if (p && !visibilityArmed) {
      document.addEventListener("visibilitychange", onVisibilityChange);
      visibilityArmed = true;
    } else if (!p && visibilityArmed) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      visibilityArmed = false;
    }
  }
  notify();
};

// Reload the page, or queue the reload (see above). Returns "now" or
// "deferred". `subject` / `verb` word the notice: "<subject> — <verb>", e.g.
// "Location switched to Odessa — Refresh to finish".
export function reloadPage({ reason = "reload", subject = "", verb = "Refresh to finish" } = {}) {
  if (!mustDefer()) {
    browser.reload();
    return "now";
  }
  // A redirect already queued loads a fresh page anyway. A location switch
  // replaces another queued reload; otherwise the first one keeps its wording.
  if (!pending || (pending.kind === "reload" && (reason === "location" || pending.reason === reason))) {
    setPending({ kind: "reload", reason, subject, verb });
  }
  return "deferred";
}

// Leave for `href` (replace = no history entry, as window.location.replace),
// or queue it. Returns "now" or "deferred".
export function leavePage(href, { replace = true, reason = "leave" } = {}) {
  if (!mustDefer()) {
    // A redirect is already queued (its "Sign in now" notice is up): a later
    // failed call joins it instead of leaving mid-typing or mid-dialog.
    if (pending && pending.kind === "redirect" && !boundaryIsSafe()) return "deferred";
    go(href, replace);
    return "now";
  }
  // The first redirect wins; it replaces a queued reload.
  if (!pending || pending.kind !== "redirect") {
    setPending({ kind: "redirect", href, replace, reason });
  }
  return "deferred";
}

// Send the browser to the sign-in page (api.js / dallasapi.js on a failed
// token refresh), or queue it. The LiveScribe socket does not need the token
// after it connects (BlinkScribe checks it once, at connect, and ignores its
// expiry), so the recording can safely finish first.
export function redirectToLogin() {
  return leavePage(`${process.env.PUBLIC_URL || ""}/login`, { reason: "signin" });
}

// A reload the user asked for with a click (the header logo, Home): skipped
// while something is busy (the click still navigates in the app); otherwise
// it runs the queued load, if any, or reloads. Returns true when it loaded.
export function reloadUnlessBusy() {
  if (isNavigationBusy()) return false;
  if (!runPendingNavigation()) browser.reload();
  return true;
}

export const getPendingNavigation = () => pending;

export function subscribePendingNavigation(fn) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// Run the queued load now (the notice's button, which is offered only once
// nothing is busy). Returns true when something ran.
export function runPendingNavigation() {
  const p = pending;
  if (!p) return false;
  setPending(null);
  if (p.kind === "redirect") go(p.href, p.replace);
  else browser.reload();
  return true;
}

// A text field has focus (not a <select>: the location switcher itself keeps
// focus after a change).
const typing = () => {
  if (typeof document === "undefined" || !document.activeElement) return false;
  const el = document.activeElement;
  return !!el.isContentEditable || /^(INPUT|TEXTAREA)$/.test(el.tagName || "");
};

// A dialog, drawer (the header chat), menu or full-screen overlay is open --
// the same test the dashboard's background refresh uses (brainycarehome.jsx).
const OVERLAY_SELECTOR = [
  '[role="dialog"]',
  '[aria-modal="true"]',
  ".MuiModal-root:not(.MuiModal-hidden)",
  ".fixed.inset-0",
].join(", ");
const overlayOpen = () => {
  if (typeof document === "undefined") return false;
  try {
    return !!document.querySelector(OVERLAY_SELECTOR);
  } catch (_) {
    return false;
  }
};

export const userMidTask = () => typing() || overlayOpen();

// May a load run by itself now? Nothing busy, the chart closed, and the
// provider not typing or in a dialog. (useAutoUpdate uses it too.)
export const boundaryIsSafe = () =>
  !isNavigationBusy() && !rec.chartOpen && !userMidTask();

// A natural boundary ("chart" closed, "route" changed, tab "visible" again):
// runs the queued load when that is safe. Returns true when it ran.
export function navigationBoundary(_kind) {
  if (!pending || !boundaryIsSafe()) return false;
  // Coming back to the tab is not a moment the provider chose: a location
  // switch's reload waits for the chart closing, a page change or Refresh
  // (a half-filled form on the page would otherwise be lost unannounced).
  if (_kind === "visible" && pending.kind === "reload" && pending.reason === "location") return false;
  return runPendingNavigation();
}

// Called by RecordingContext whenever recording / note processing / chart-open
// changes. It never runs a queued load by itself, except at the chart closing,
// which is a natural boundary.
export function setRecordingState(next) {
  const wasChartOpen = rec.chartOpen;
  rec = { ...rec, ...next };
  if (!rec.chartOpen) rec.chartNote = false;
  else if (isNavigationBusy()) rec.chartNote = true;
  notify();
  if (wasChartOpen && !rec.chartOpen) navigationBoundary("chart");
}

// A screen that records outside RecordingContext (Editscreen's Edit with
// Voice, the chat voice message, ...) holds navigation while its recording,
// and whatever it still needs the audio for, is in progress. Returns the
// release function (safe to call more than once). React: useNavigationHold.
export function holdNavigation() {
  localHolds += 1;
  if (rec.chartOpen) rec = { ...rec, chartNote: true };
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    localHolds = Math.max(0, localHolds - 1);
    notify();
  };
}

// --- Wording -----------------------------------------------------------------

const STILL = {
  recording: "Your recording is still running",
  voice: "Your voice recording is still running",
  note: "Your note is still processing",
};
const AFTER = {
  recording: "your recording ends",
  voice: "your voice recording ends",
  note: "your note finishes processing",
};
const AFTER_IT = { recording: "it ends", voice: "it ends", note: "it's done" };

// What DeferredNavigationNotice shows for the queued load: { text, action }
// (action = the button label, only once nothing is busy), or null for none.
// A queued location-switch reload shows nothing while the chart is open: the
// switch's toast said closing the chart refreshes the page, and the notice
// would sit on top of the chart for the whole visit.
export function pendingNotice(p = pending) {
  if (!p) return null;
  const busy = busyReason();
  if (p.kind === "redirect") {
    if (busy) {
      return {
        text: `Your sign-in has expired. ${STILL[busy]}; you'll be asked to sign in after ${AFTER_IT[busy]}.`,
        action: null,
      };
    }
    return { text: "Your sign-in has expired.", action: "Sign in now" };
  }
  if (rec.chartOpen && p.reason === "location") return null;
  const subject = p.subject || "This page needs a refresh";
  const verb = p.verb || "Refresh to finish";
  if (busy) return { text: `${subject}. ${verb} after ${AFTER[busy]}.`, action: null };
  return { text: `${subject} — ${verb}`, action: "Refresh" };
}

// Tests only.
export function resetDeferredNavigationForTests() {
  rec = { recording: false, processing: false, chartOpen: false, chartNote: false };
  localHolds = 0;
  listeners.clear();
  setPending(null);
}
